import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { map, Observable, of, take } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class BiTranslateService extends TranslateService {
  public get(key: string, interpolateParams?: { [key: string]: string | number }): Observable<string> {
    if (!key) throw new Error(`Parameter "key" required`);

    return super.get(key.toUpperCase(), interpolateParams);
  }

  /**
   * Sometimes, the Ngx TranslateService fails at getting a translation, which is likely because it doesn't wait until translations are loaded.
   * We're not quite sure but why but this method makes sure to look in cache first and, if not finding anything, wait until language has changed/has been set
   * and translations for the language has been loaded.
   * @param key The translation key
   * @returns Observable containing the translation
   */
  public getTranslationSafe(key: string) {
    if (this.translations && this.translations[this.currentLang]) return of(this.translations[this.currentLang][key.toUpperCase()]);
    else {
      return this.onLangChange.pipe(
        take(1),
        map(e => e.translations[key.toUpperCase()])
      );
    }
  }

  /**
   * Get translations of multiple keys
   * @param key MUST BE UPPERCASE!!
   * @returns Observable with an object of keys being the translation keys and the values being the translated string
   */
  public getMulti(key: Array<string>, interpolateParams?: { [key: string]: string | number }): Observable<{ [key: string]: string }> {
    if (!key) throw new Error(`Parameter "key" required`);

    return super.get(
      key.map(k => k.toUpperCase()),
      interpolateParams
    );
  }

  public instant(key: string, interpolateParams?: { [key: string]: string | number }): string {
    if (!key) throw new Error(`Parameter "key" required`);

    return super.instant(key.toUpperCase(), interpolateParams);
  }

  /**
   * Get translations of multiple keys
   * @param key MUST BE UPPERCASE!!
   * @returns An object of keys being the translation keys and the values being the translated string
   */
  public instantMulti(keys: Array<string>, interpolateParams?: { [key: string]: string | number }): { [key: string]: string } {
    if (!keys) throw new Error(`Parameter "key" required`);

    return super.instant(
      keys.map(k => k.toUpperCase()),
      interpolateParams
    );
  }
}
